import * as React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  createStyles,
  Link,
  makeStyles,
  Theme,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { OpenInNewWindow } from '@bb-ui/icons/dist/small';

export interface SiteInformationItemProps {
  title: string;
  value?: string | number;
  href?: string;
}

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      border: 'none',
      display: 'grid',
      gridSpacing: theme.spacing(2),
      gridTemplateColumns: '1fr 1fr',
      marginBottom: '1px',
    },
    cardContent: {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.fontSizeSmall,
      textAlign: 'right',
    },
    cardHeader: {
      fontSize: theme.typography.fontSizeSmall,
      fontWeight: theme.typography.fontWeightBold,
    },
    unsetValue: {
      fontStyle: 'italic',
    },
    // Same look as the tenant links on the tenant information page
    tenantLink: {
      color: theme.palette.text.secondary,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }),
);

export const LearnSiteInformationItem: React.FC<SiteInformationItemProps> = (props) => {
  const { title, value, href } = props;
  const classes = useStyles(props);
  const { t } = useTranslation();
  const isUrl = title.toLowerCase() === 'url';

  const linkTo = href ?? (isUrl && value ? `https://${value}` : undefined);

  const isAppLink = linkTo?.startsWith('/');

  let displayValue: React.ReactNode = value;
  if (linkTo && value && isAppLink) {
    displayValue = (
      <a href={linkTo} target="_blank" rel="noopener noreferrer" className={classes.tenantLink}>
        {value}
      </a>
    );
  } else if (linkTo && value) {
    displayValue = (
      <Link href={linkTo} target="_blank" rel="noopener noreferrer">
        {value} <OpenInNewWindow />
      </Link>
    );
  }
  return (
    <Card className={classes.card}>
      <CardHeader title={title} data-testid="learn-site-info-item-title" />
      <CardContent className={classes.cardContent} data-testid="learn-site-info-item-content">
        {displayValue ?? (
          <span className={classes.unsetValue}>{t('learnSiteInformation.unset')}</span>
        )}
      </CardContent>
    </Card>
  );
};
